import React, { ComponentProps, useCallback, useEffect, useMemo, useState } from 'react';
import Product, { maxMakeable } from '../../graphql/entities/product/Product';
import PrimaryFieldFormatter from '../../components/grid-addons/formatters/PrimaryFieldFormatter';
import ChannelFormatter from '../../components/grid-addons/formatters/ChannelFormatter';
import attributeQuery from '../../graphql/queries/product/ProductAttributeConfigListQuery.graphql';
import { usePredefinedFilters } from '../../getPredefinedFilters';
import { useProductConfig } from '../../utils/FetchConfig';
import { useWorkspace, WorkspaceConfig } from '../../graphql/hooks/useWorkspace';
import { CostViewPermission, IFilterInput, IProduct, IProductEdge, Maybe } from '../../graphql/schema/p4types';
import Workspace from '../../components/Workspace';
import { useAttributesForWorkspace } from '../../components/Workspace/useAttributesForWorkspace';
import { IDependentValues, IWorkspaceColumnOverride, IWorkspaceProps } from '../../@types/workspaceTypes';
import { Paths } from '../../@types/keyOfNested';
import { useProductWorkspaceActions } from '../../actions/product/ProductWorkspaceAction';
import { WorkspaceActionButtons } from '../../components/Workspace/WorkspaceActionButtons';
import getIn from '../../components/connectedForm/utils/getIn';
import useWarehouseColumns from '../../utils/useWarehouseColumns';
import { IColumnFromArrayDetailField, ICustomColumn } from '../../utils/setColumnsFromArray';
import AssemblyAttributeConfigListQuery from '../../graphql/queries/product/AssemblyAttributeConfigListQuery.graphql';
import { useFullUserDetails, usePrecision } from '../../utils/hooks';
import { IEditableDataGridColumnDef } from '../../@types/editableDataGridTypes';
import Formatters from '../../components/grid-addons/formatters';
import fieldMetaData from '../../graphql/schema/fieldMetaData';
import { useWorkspaceSelection } from '../../components/Workspace/useWorkspaceSelection';
import { NumberRange } from '../../@types/numberRange';
import AliasFormatter from '../../components/grid-addons/formatters/AliasFormatter';

const { workspace } = Product;
const LazyProductForm = React.lazy(() => import('./ProductForm'));

export const ProductCellRenderer = ({ dependentValues }: { dependentValues: IDependentValues<IProductEdge> }) => {
  if (!dependentValues.rowData?.node) return <div />;
  const { reference, description } = dependentValues.rowData.node;
  return (
    <div>
      <div className="cell-primary">{reference}</div>
      <div className="cell-data">{description}</div>
    </div>
  );
};

const productWorkspaceFormatDecimals = (rowData: IProduct): number => {
  return rowData.precision ?? 7;
};

const PRODUCT_FIELD_NAMES: Array<Paths<IProduct>> = [
  'reference',
  'description',
  'latestCostPrice',
  'costUnitDescription',
  'sellingPrices.sellingPrice1',
  'sellingUnitDescription',
  'vatProductCategory.description',
];

export const PRODUCT_WORKSPACE_DETAIL_FIELDS: Array<IColumnFromArrayDetailField> = [
  {
    title: 'Free Stock',
    fieldName: 'freeStock',
    formatter: 'numeric',
    options: { format: { decimals: productWorkspaceFormatDecimals } },
  },
  {
    title: 'On Order',
    fieldName: 'onOrder',
    formatter: 'numeric',
    options: { format: { decimals: productWorkspaceFormatDecimals } },
  },
  {
    title: 'Makeable',
    fieldName: 'makeable',
    formatter: 'numeric',
    options: {
      infinityLimit: maxMakeable,
      format: { decimals: productWorkspaceFormatDecimals },
    },
  },
  {
    title: 'Default Bin Location',
    fieldName: 'defaultBinLocation.location',
    type: 'String',
  },
  {
    title: 'Minimum Levels',
    fieldName: 'minimumLevel',
    formatter: 'numeric',
    options: { format: { decimals: productWorkspaceFormatDecimals } },
  },
  {
    title: 'Optimal Levels',
    fieldName: 'optimumLevel',
    formatter: 'numeric',
    options: { format: { decimals: productWorkspaceFormatDecimals } },
  },
  {
    title: 'Maximum Levels',
    fieldName: 'maximumLevel',
    formatter: 'numeric',
    options: { format: { decimals: productWorkspaceFormatDecimals } },
  },
  {
    title: 'Level',
    fieldName: 'level',
    formatter: 'numeric',
    options: { format: { decimals: productWorkspaceFormatDecimals } },
  },
  {
    title: 'Last Stock Take Created',
    fieldName: 'lastStockTakeCreated',
    formatter: 'dateTime',
    type: 'String',
  },
  {
    title: 'Last Stock Take Updated',
    fieldName: 'lastStockTakeUpdated',
    formatter: 'dateTime',
    type: 'String',
  },
  {
    title: 'Date of Last Receipt',
    fieldName: 'dateOfLastReceipt',
    formatter: 'dateTime',
    type: 'LocalDateTime',
  },
  {
    title: 'Date of Last Issue',
    fieldName: 'dateOfLastIssue',
    formatter: 'dateTime',
    type: 'LocalDateTime',
  },
];

const useProductDefaultColumns = (): IWorkspaceColumnOverride => {
  const costPricePrecision = usePrecision('cost');
  const sellingPricePrecision = usePrecision('sales');

  return useMemo(
    () => ({
      reference: {
        formatter: PrimaryFieldFormatter,
      },
      weight: {
        formatter: 'numeric',
        options: { format: { decimals: 2 } },
      },
      'packagingDimensions.volume': {
        formatter: 'numeric',
        options: { format: { decimals: 9 }, suppressTrailingZeros: true, suppressZeroes: true },
      },
      'channels.description': {
        formatter: ChannelFormatter,
        name: 'Channels',
        sortable: false,
      },
      costUnitDescription: {
        name: 'Cost Unit',
      },
      sellingUnitDescription: {
        name: 'Sales Unit',
      },
      'vatProductCategory.description': {
        name: 'Vat Category',
      },
      mainSupplierDetails: {
        sortable: true,
      },
      'mainSupplierDetails.partNumber': {
        name: 'Main Supplier - Part Number',
        sortable: false,
      },
      'mainSupplierDetails.supplier.name': {
        name: 'Main Supplier - Name',
        sortable: false,
      },
      'mainSupplierDetails.supplier.reference': {
        name: 'Main Supplier - Reference',
        sortable: false,
      },
      makeable: {
        formatter: 'numeric',
        options: {
          infinityLimit: maxMakeable,
          format: { decimals: productWorkspaceFormatDecimals },
        },
      },
      'mainSupplierDetails.orderQuantityMinimum': {
        name: 'Main Supplier - Minimum Order Quantity',
        sortable: false,
      },
      orderQuantityMinimum: {
        name: 'Minimum Order Quantity',
      },
      'mainSupplierDetails.leadTimeDays': {
        name: 'Main Supplier - Lead Time Days',
        sortable: false,
      },
      'mainSupplierDetails.stockLevel': {
        name: 'Main Supplier - Stock Level',
        sortable: false,
      },
      freeStock: {
        name: 'Free Stock',
        options: { format: { decimals: productWorkspaceFormatDecimals } },
        formatter: 'numeric',
      },
      onOrder: {
        name: 'On Order',
        options: { format: { decimals: productWorkspaceFormatDecimals } },
        formatter: 'numeric',
      },
      'luckinsItem.itemCode': {
        name: 'Luckins - TSI Code',
      },
      'alias.reference': {
        name: 'Alias References',
        formatter: AliasFormatter,
      },
      sellingUnitQuantity: {
        options: { format: { decimals: productWorkspaceFormatDecimals } },
        formatter: 'numeric',
      },
      level: {
        title: 'Level',
        formatter: 'numeric',
        options: { format: { decimals: productWorkspaceFormatDecimals } },
      },
      windsorFrameworkAtRiskStatus: {
        name: 'Windsor Framework Status',
        formatter: Formatters.EnumFormatter,
        options: fieldMetaData.WindsorFrameworkAtRiskStatus.enumValues,
      },
      costUnitQuantity: {
        name: 'Cost Unit Quantity',
        formatter: 'numeric',
        options: { format: { decimals: productWorkspaceFormatDecimals } },
      },
      hasAlternateUnits: {
        name: 'Has Alternate Units',
        sortable: false,
        formatter: Formatters.BooleanFormatter,
        tooltip:
          'Dynamic column - Sorting, filtering, and dynamic reporting are unavailable due to dynamic generation of value',
        styles: { headerCell: { backgroundColor: '#d0edff' } },
      },

      ...getPriceColumnOverrides(costPricePrecision, sellingPricePrecision),
    }),
    [costPricePrecision, sellingPricePrecision]
  );
};

const ProductWorkspaceActions = (props: ComponentProps<typeof WorkspaceActionButtons>) => {
  const [productWorkspaceActions, productWorkspaceActionModals] = useProductWorkspaceActions();
  return (
    <>
      {productWorkspaceActionModals}
      <WorkspaceActionButtons {...props} actionButtons={productWorkspaceActions} />
    </>
  );
};

/**
 * @param prefix - Optional prefix for column overrides - Used for product usage workspace where all product fields are nested like 'product.X'
 */
export const useProductColumnOverrides = (prefix: string = ''): IWorkspaceColumnOverride | undefined => {
  const [columnOverrides, setColumnOverrides] = useState<IWorkspaceColumnOverride>();
  const productConfig = useProductConfig();
  const costPricePrecision = usePrecision('cost');
  const sellingPricePrecision = usePrecision('sales');

  const productDefaultColumnOverrides = useProductDefaultColumns();

  useEffect(() => {
    const defaultOverrides: { [key: string]: IEditableDataGridColumnDef } = {};
    Object.keys(productDefaultColumnOverrides).map((key) => {
      defaultOverrides[`${prefix}${key}`] = productDefaultColumnOverrides[key];
    });

    if (productConfig) {
      const priceAttributeOverrides: IWorkspaceColumnOverride = {};
      for (let i = 1; i <= 10; i++) {
        const pricingAttributeTitle = getIn(productConfig, `pricingAttributeTitle${i}`);
        if (pricingAttributeTitle) {
          priceAttributeOverrides[`${prefix}pricingAttributes.pricingAttribute${i}.description`] = {
            name: `Pricing Attribute - ${pricingAttributeTitle}`,
          };
          //Add a root one for the "List" variety of the filter - 'title' needs to be set for this one.
          //Has a different name to help distinguish it.
          priceAttributeOverrides[`${prefix}pricingAttributes.pricingAttribute${i}`] = {
            name: `Pricing - ${pricingAttributeTitle}`,
            title: `Pricing - ${pricingAttributeTitle}`,
          };
        } else {
          priceAttributeOverrides[`${prefix}pricingAttributes.pricingAttribute${i}`] = {
            name: `Pricing - ${i}`,
            title: `Pricing - ${i}`,
          };
        }
      }

      const sellingPriceOverrides: IWorkspaceColumnOverride = {};
      for (let i = 1; i < 11; i++) {
        const sellingPriceTitle = productConfig[`sellingPriceTitle${i as NumberRange<1, 10>}`];
        if (sellingPriceTitle) {
          sellingPriceOverrides[`${prefix}sellingPrices.sellingPrice${i}`] = {
            name: `Selling Price ${sellingPriceTitle}`,
            formatter: 'numeric',
            options: { format: { decimals: sellingPricePrecision } },
            cellClass: 'rightAlign',
            sortable: false,
          };
          if (i > 1) {
            sellingPriceOverrides[`${prefix}sellingPrices.sellingPriceBreak${i}`] = {
              name: `Selling Price ${sellingPriceTitle} - Break`,
              formatter: 'numeric',
              options: { format: { decimals: sellingPricePrecision } },
              cellClass: 'rightAlign',
              sortable: false,
            };
          }
        }
      }

      const mainSupplierCostPriceOverrides: IWorkspaceColumnOverride = {};
      for (let i = 1; i <= 10; i++) {
        const mainSupplierCostPriceTitle = getIn(productConfig, `costPriceTitle${i}`);
        if (mainSupplierCostPriceTitle) {
          mainSupplierCostPriceOverrides[`${prefix}mainSupplierDetails.costPrices.costPrice${i}`] = {
            ...mainSupplierCostPriceOverrides[`mainSupplierDetails.costPrices.costPrice${i}]`],
            name: `Main Supplier ${mainSupplierCostPriceTitle}`,
            formatter: 'numeric',
            options: { format: { decimals: costPricePrecision } },
            cellClass: 'rightAlign',
            sortable: false,
          };

          if (i > 1) {
            mainSupplierCostPriceOverrides[`${prefix}mainSupplierDetails.costPrices.costPriceBreak${i}`] = {
              ...mainSupplierCostPriceOverrides[`mainSupplierDetails.costPrices.costPriceBreak${i}]`],
              name: `Main Supplier ${mainSupplierCostPriceTitle} - Break`,
              formatter: 'numeric',
              options: { format: { decimals: costPricePrecision } },
              cellClass: 'rightAlign',
              sortable: false,
            };
          }
        }
      }

      setColumnOverrides({
        ...defaultOverrides,
        ...sellingPriceOverrides,
        ...priceAttributeOverrides,
        ...mainSupplierCostPriceOverrides,
      });
      return;
    }

    setColumnOverrides(defaultOverrides);
  }, [costPricePrecision, prefix, productConfig, sellingPricePrecision]);

  return columnOverrides;
};

const getPriceColumnOverrides = (
  costPricePrecision: number,
  sellingPricePrecision: number
): IWorkspaceColumnOverride => {
  let priceColumnOverrides: IWorkspaceColumnOverride = {
    'sellingPrices.minimumPrice': {
      name: `Selling Prices - Minimum Price`,
      formatter: 'numeric',
      options: { format: { decimals: sellingPricePrecision } },
      cellClass: 'rightAlign',
    },
    latestCostPrice: {
      name: 'Latest Cost',
      formatter: 'numeric',
      options: { format: { decimals: costPricePrecision } },
      cellClass: 'rightAlign',
    },
    haggleCostPrice: {
      name: 'Haggle Cost',
      formatter: 'numeric',
      options: { format: { decimals: costPricePrecision } },
      cellClass: 'rightAlign',
    },
    valuationCostPrice: {
      name: 'Valuation Cost',
      formatter: 'numeric',
      options: { format: { decimals: costPricePrecision } },
      cellClass: 'rightAlign',
    },
  };

  for (let i = 1; i < 11; i++) {
    priceColumnOverrides[`sellingPrices.sellingPrice${i}`] = {
      name: `Selling Price ${i}`,
      formatter: 'numeric',
      options: { format: { decimals: sellingPricePrecision } },
      cellClass: 'rightAlign',
    };
    priceColumnOverrides[`mainSupplierDetails.costPrices.costPrice${i}`] = {
      name: `Cost Price ${i}`,
      formatter: 'numeric',
      options: { format: { decimals: costPricePrecision } },
      cellClass: 'rightAlign',
    };
  }

  return priceColumnOverrides;
};

type IProductWorkspaceProps = WorkspaceConfig<IProduct> &
  IWorkspaceProps<IProduct> & {
    predefinedFilters?: Array<IFilterInput>;
    setWorkspaceRef?: any;
  };

/**
 * Extracted logic for defining a Product Workspace out to allow us to also extract the workspaceSelection configuration out for shortage criteria.
 */
export const useProductWorkspace = ({
  predefinedFilters: predefinedFiltersFromProps,
  columns,
  customColumns: customColumnsFromProps,
  workspaceConfig,
}: {
  predefinedFilters?: Array<IFilterInput>;
  columns?: Array<Paths<IProduct>>;
  customColumns?: Maybe<{ [key: string]: ICustomColumn }>;
  workspaceConfig?: WorkspaceConfig<IProduct>;
}) => {
  const warehouseColumns = useWarehouseColumns('productWarehouseDetails', PRODUCT_WORKSPACE_DETAIL_FIELDS);

  const predefinedFilters = usePredefinedFilters({ predefinedFilters: predefinedFiltersFromProps });
  const attributeColumns = useAttributesForWorkspace(attributeQuery);
  const assemblyAttributeColumns = useAttributesForWorkspace(AssemblyAttributeConfigListQuery, {
    columnKey: 'assemblyAttributeValues.attributeConfig.title',
    valuesPath: 'assemblyAttributeValues',
  });

  const columnOverrides = useProductColumnOverrides();

  const customColumns = useMemo(() => {
    return {
      ...warehouseColumns,
      ...attributeColumns,
      ...assemblyAttributeColumns,
      ...(customColumnsFromProps || {}),
    };
  }, [warehouseColumns, attributeColumns, assemblyAttributeColumns, customColumnsFromProps]);

  const displayColumns = useMemo(() => {
    return [...(PRODUCT_FIELD_NAMES as Array<string>), ...(columns || [])] as Array<Paths<IProduct>>;
  }, [columns]);

  const fullUserDetails = useFullUserDetails();

  const workspaceProps = useWorkspace<IProduct>(workspaceConfig!, Product, {
    filters: predefinedFilters,
    columns: displayColumns,
    customColumns: customColumns,
    columnOverrides: columnOverrides,
    initialSortColumn: 'reference',
  });

  // Get the users permissions for displaying costs, default to most secure (None) if null or undefined.
  const costViewPermission: CostViewPermission = fullUserDetails?.companyUser?.costViewPermission
    ? fullUserDetails?.companyUser?.costViewPermission
    : CostViewPermission.None;

  const availableColumnsFilter = useCallback(
    (column: IEditableDataGridColumnDef): boolean => {
      if (!column?.fieldName) {
        return false;
      }

      const fieldName: string = column.fieldName;

      if (fieldName.includes('lastStockTakeCreated') || fieldName.includes('lastStockTakeUpdated')) {
        return false;
      }

      let showColumn: boolean = true;

      switch (costViewPermission) {
        case CostViewPermission.All:
          // Do nothing, we can allow it all
          break;
        case CostViewPermission.Haggle:
          // Disallow ALL cost fields EXCEPT latestCost as that will be set by services to be the haggle cost.
          if (
            fieldName === 'costUnitQuantity' ||
            fieldName === 'costUnitDescription' ||
            fieldName === 'valuationCostPrice' ||
            fieldName === 'haggleCostPrice' ||
            fieldName === 'costPriceReviewDays' ||
            fieldName === 'overallValuation' ||
            fieldName.startsWith('mainSupplierDetails.costPrices.costPrice')
          ) {
            showColumn = false;
          }
          break;
        case CostViewPermission.None:
          // Disallow ALL cost fields if we are not allowed to see costs
          if (
            fieldName === 'costUnitQuantity' ||
            fieldName === 'costUnitDescription' ||
            fieldName === 'valuationCostPrice' ||
            fieldName === 'latestCostPrice' ||
            fieldName === 'haggleCostPrice' ||
            fieldName === 'costPriceReviewDays' ||
            fieldName === 'overallValuation' ||
            fieldName.startsWith('mainSupplierDetails.costPrices.costPrice')
          ) {
            showColumn = false;
          }
          break;
      }

      return showColumn;
    },
    [costViewPermission]
  );

  return useMemo(
    () => ({
      workspaceProps,
      availableColumnsFilter,
    }),
    [workspaceProps, availableColumnsFilter]
  );
};

/**
 * Helper hook just to extract the product workspace setup logic.
 * Used for shortages so the selection state can be handled by the generation forms, rather than leaving it to the Workspace itself.
 */
export const useProductWorkspaceWithSelection = ({
  viewScreenName,
  predefinedFilters,
}: {
  viewScreenName: string;
  predefinedFilters?: Array<IFilterInput>;
}) => {
  const { workspaceProps: productWorkspaceProps, availableColumnsFilter: productAvailableColumnsFilter } =
    useProductWorkspace({
      predefinedFilters: predefinedFilters ?? [],
      workspaceConfig: { viewScreenName },
    });

  const productWorkspaceSelection = useWorkspaceSelection({
    enableRowSelect: true, // enable row selection by default
    listData: productWorkspaceProps.listData,
    viewScreenName: viewScreenName,
    workspaceFilters: productWorkspaceProps.workspaceFilters,
    initialSelectedRows: [],
    initialSelectedRowIds: [],
  });

  return useMemo(
    () => ({
      productWorkspaceProps: { ...productWorkspaceProps, viewScreenName: viewScreenName, enableRowSelect: true },
      productAvailableColumnsFilter,
      productWorkspaceSelection,
    }),
    [productAvailableColumnsFilter, productWorkspaceProps, productWorkspaceSelection, viewScreenName]
  );
};

const ProductWorkspace = ({ predefinedFilters: predefinedFiltersFromProps, ...props }: IProductWorkspaceProps) => {
  const { workspaceProps, availableColumnsFilter } = useProductWorkspace({
    predefinedFilters: predefinedFiltersFromProps,
    columns: props.columns,
    customColumns: props.customColumns,
    workspaceConfig: props, //Likely doesn't need all props, but maintaining existing behaviours.
  });

  return (
    <Workspace<IProduct>
      ref={props.setWorkspaceRef}
      cellRenderer={ProductCellRenderer}
      detailView={LazyProductForm}
      defaultQuery={workspace}
      duplicate={true}
      availableColumnsFilter={availableColumnsFilter}
      availableFiltersFilter={availableColumnsFilter}
      {...(workspaceProps as any)}
      {...props}
      entity={Product}
      workspaceActionButtonsHOC={ProductWorkspaceActions}
    />
  );
};
export default ProductWorkspace;
